import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Check, X, Minus, HelpCircle, ChevronDown, ChevronUp, Ban } from 'lucide-react';
import { buildApiUrl } from '../config';

const VoteDetails = ({ vote, billId, isDarkMode, voteDetailsCache, setVoteDetailsCache, initialExpanded, initialFilter }) => {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(initialExpanded || false);
    const [filter, setFilter] = useState(initialFilter || 'all'); // all, yea, nay, nv, absent
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState(null);

    // Check if vote details are already cached or loaded
    // Recompute when cache changes
    const cachedDetails = voteDetailsCache?.get(vote.roll_call_id);
    const voteDetails = vote.details || cachedDetails;

    const fetchVoteDetails = useCallback(async () => {
        if (!billId || !vote.roll_call_id) return;
        
        // Check cache first
        if (voteDetailsCache?.has(vote.roll_call_id)) {
            return;
        }
        
        setLoadingDetails(true);
        setDetailsError(null);
        
        try {
            const response = await fetch(buildApiUrl(`/api/bills/${billId}/vote-details`));
            if (!response.ok) {
                throw new Error('Failed to fetch vote details');
            }
            
            const data = await response.json();
            if (data.votes && Array.isArray(data.votes)) {
                // Cache all vote details
                const newCache = new Map(voteDetailsCache || []);
                data.votes.forEach(v => {
                    if (v.roll_call_id && v.details) {
                        newCache.set(v.roll_call_id, v.details);
                    }
                });
                if (setVoteDetailsCache) {
                    setVoteDetailsCache(newCache);
                }
            }
        } catch (error) {
            console.error('Error fetching vote details:', error);
            setDetailsError(error.message);
        } finally {
            setLoadingDetails(false);
        }
    }, [billId, vote.roll_call_id, voteDetailsCache, setVoteDetailsCache]);

    // Sync state when props change
    useEffect(() => {
        if (initialExpanded !== undefined) {
            setIsExpanded(initialExpanded);
        }
        if (initialFilter !== undefined) {
            setFilter(initialFilter);
        }
    }, [initialExpanded, initialFilter]);

    // Fetch vote details when expanded if not already loaded
    useEffect(() => {
        if (isExpanded && !voteDetails && billId && vote.roll_call_id && !loadingDetails) {
            fetchVoteDetails();
        }
    }, [isExpanded, billId, vote.roll_call_id, voteDetails, loadingDetails, fetchVoteDetails]);

    // Use vote.details if available (from initial load), otherwise use lazy-loaded details
    const details = vote.details || voteDetails;

    const filteredVotes = (details || []).filter(v => {
        if (filter === 'all') return true;
        const voteStr = String(v.vote || '').toLowerCase();
        if (filter === 'yea') return voteStr === 'yes' || voteStr === 'yea';
        if (filter === 'nay') return voteStr === 'no' || voteStr === 'nay';
        // Match NV, No Vote, excused, and not voting for consistency
        if (filter === 'nv') return voteStr === 'nv' || voteStr === 'no vote' || voteStr === 'excused' || voteStr === 'not voting';
        if (filter === 'absent') return voteStr === 'absent';
        return true;
    });

    const getVoteIcon = (voteValue) => {
        const v = String(voteValue || '').toLowerCase();
        if (v === 'yes' || v === 'yea') return <Check className="w-3 h-3 text-green-500" />;
        if (v === 'no' || v === 'nay') return <X className="w-3 h-3 text-red-500" />;
        if (v === 'nv' || v === 'no vote' || v === 'not voting' || v === 'excused') return <Ban className="w-3 h-3 text-yellow-500" />;
        return <HelpCircle className="w-3 h-3 text-gray-400" />;
    };

    const getVoteColor = (voteValue) => {
        const v = String(voteValue || '').toLowerCase();
        if (v === 'yes' || v === 'yea') return isDarkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-100';
        if (v === 'no' || v === 'nay') return isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-100';
        if (v === 'nv' || v === 'no vote' || v === 'not voting' || v === 'excused') return isDarkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-100';
        if (v === 'absent') return isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100';
        return isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100';
    };

    const getVoteLink = (voteDetail) => {
        // If they have a politician ID, link to their profile on CivicDuty
        if (voteDetail.politicianId) {
            return `/politicians/${voteDetail.politicianId}`;
        }
        // If they have legislator data with gga_member_id and chamber, link to www.legis.ga.gov
        if (voteDetail.ggaMemberId && voteDetail.chamber) {
            const chamberPath = voteDetail.chamber.toLowerCase() === 'senate' ? 'senate' : 'house';
            return `https://www.legis.ga.gov/members/${chamberPath}/${voteDetail.ggaMemberId}`;
        }
        return null;
    };

    const handleVoteClick = (voteDetail, e) => {
        const link = getVoteLink(voteDetail);
        if (link) {
            if (link.startsWith('http')) {
                // External link - open in new tab
                window.open(link, '_blank', 'noopener,noreferrer');
            } else {
                // Internal link - navigate
                navigate(link);
            }
        }
    };

    return (
        <div className="mt-3">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={`flex items-center gap-1 text-xs font-semibold ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} transition-colors`}
            >
                {isExpanded ? (
                    <>
                        <ChevronUp className="w-3 h-3" />
                        Hide Individual Votes
                    </>
                ) : (
                    <>
                        <ChevronDown className="w-3 h-3" />
                        Show Individual Votes ({details ? details.length : vote.total || 0} records)
                    </>
                )}
            </button>

            {isExpanded && (
                <div className={`mt-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} animate-in fade-in slide-in-from-top-1`}>
                    {loadingDetails && (
                        <div className={`text-center py-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Loading vote details...
                        </div>
                    )}
                    
                    {detailsError && (
                        <div className={`text-center py-4 text-xs italic ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                            Failed to load vote details: {detailsError}
                        </div>
                    )}

                    {!loadingDetails && !detailsError && (!details || details.length === 0) && (
                        <div className={`text-center py-4 text-xs italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Detailed vote records not available.
                        </div>
                    )}

                    {!loadingDetails && !detailsError && details && details.length > 0 && (
                        <>
                            {/* Filters */}
                            <div className="flex gap-2 mb-3 overflow-x-auto pb-1 custom-scrollbar">
                                {['all', 'yea', 'nay', 'nv', 'absent'].map(f => (
                                    <button
                                        key={f}
                                        onClick={() => setFilter(f)}
                                        className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${filter === f
                                                ? (isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white')
                                                : (isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white border text-gray-600 hover:bg-gray-100')
                                            }`}
                                    >
                                        {f === 'all' ? 'All Votes' : f === 'nv' ? 'No Vote' : f === 'absent' ? 'Absent' : f}
                                    </button>
                                ))}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                                {filteredVotes.map((v, idx) => {
                                    const hasLink = getVoteLink(v) !== null;
                                    const VoteComponent = hasLink ? 'button' : 'div';
                                    return (
                                        <VoteComponent
                                            key={idx}
                                            onClick={hasLink ? (e) => handleVoteClick(v, e) : undefined}
                                            className={`flex items-center gap-2 p-2 rounded border ${getVoteColor(v.vote)} ${hasLink ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                                        >
                                            <div className="w-6 h-6 rounded-full bg-gray-300 overflow-hidden flex-shrink-0">
                                                {v.photoUrl ? (
                                                    <img src={v.photoUrl} alt={v.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <User className="w-4 h-4 m-1 text-gray-500" />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className={`text-xs font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                                    {v.name}
                                                </div>
                                                <div className={`text-[10px] truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {v.party}
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0">
                                                {getVoteIcon(v.vote)}
                                            </div>
                                        </VoteComponent>
                                    );
                                })}

                                {filteredVotes.length === 0 && (
                                    <div className={`col-span-full text-center py-4 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        No votes found for this filter.
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default VoteDetails;
